import { generateClient } from 'aws-amplify/api';
import { GraphQLResult } from '@aws-amplify/api-graphql';

import { ConversationService } from './conversationService';
import { ScheduleService } from './scheduleService';
import { ShareService } from './shareService';

const client = generateClient();

export interface UserProject {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  owner?: string;
  organizationId?: string;
  datasetIds?: string[];
  createdAt?: string;
  updatedAt?: string;
  tasks?: { items: UserProjectTask[] };
}

export interface UserProjectTask {
  id: string;
  projectId: string;
  title: string;
  description: string;
  order: number;
  completedTask?: boolean;
  owner?: string;
}

const CREATE_PROJECT = `
  mutation CreateUserProject($input: CreateUserProjectInput!) {
    createUserProject(input: $input) {
      id
      title
      description
      imageUrl
      owner
      organizationId
      datasetIds
      createdAt
      updatedAt
    }
  }
`;

const CREATE_PROJECT_TASK = `
  mutation CreateUserProjectTask($input: CreateUserProjectTaskInput!) {
    createUserProjectTask(input: $input) {
      id
      projectId
      title
      description
      order
      completedTask
      owner
    }
  }
`;

const LIST_PROJECTS = `
  query ListUserProjects {
    listUserProjects {
      items {
        id
        title
        description
        imageUrl
        owner
        organizationId
        datasetIds
        createdAt
        updatedAt
        tasks {
          items {
            id
          }
        }
      }
    }
  }
`;

const UPDATE_PROJECT = `
  mutation UpdateUserProject($input: UpdateUserProjectInput!) {
    updateUserProject(input: $input) {
      id
      title
      description
      imageUrl
      owner
      organizationId
      datasetIds
      createdAt
      updatedAt
    }
  }
`;

const DELETE_PROJECT = `
  mutation DeleteUserProject($input: DeleteUserProjectInput!) {
    deleteUserProject(input: $input) {
      id
    }
  }
`;

const DELETE_PROJECT_TASK = `
  mutation DeleteUserProjectTask($input: DeleteUserProjectTaskInput!) {
    deleteUserProjectTask(input: $input) {
      id
    }
  }
`;

const GET_PROJECT_TASKS = `
  query GetProjectTasks($filter: ModelUserProjectTaskFilterInput) {
    listUserProjectTasks(filter: $filter) {
      items {
        id
        projectId
        title
        description
        order
        completedTask
        owner
      }
    }
  }
`;

const UPDATE_PROJECT_TASK = `
  mutation UpdateUserProjectTask($input: UpdateUserProjectTaskInput!) {
    updateUserProjectTask(input: $input) {
      id
      projectId
      title
      description
      order
      completedTask
      owner
    }
  }
`;

export class ProjectService {
  static async listProjects(): Promise<UserProject[]> {
    const result = await client.graphql({
      query: LIST_PROJECTS,
    }) as GraphQLResult<{ listUserProjects: { items: UserProject[] } }>;

    return result.data?.listUserProjects?.items ?? [];
  }

  static async createProject(input: {
    title: string;
    description: string;
    imageUrl?: string;
    organizationId?: string;
  }): Promise<UserProject> {
    const result = await client.graphql({
      query: CREATE_PROJECT,
      variables: { input },
    }) as GraphQLResult<{ createUserProject: UserProject }>;

    if (!result.data?.createUserProject) {
      throw new Error('Failed to create project');
    }

    return result.data.createUserProject;
  }

  static async createProjectTask(input: {
    projectId: string;
    title: string;
    description: string;
    order: number;
  }): Promise<UserProjectTask> {
    const result = await client.graphql({
      query: CREATE_PROJECT_TASK,
      variables: { input },
    }) as GraphQLResult<{ createUserProjectTask: UserProjectTask }>;

    if (!result.data?.createUserProjectTask) {
      throw new Error('Failed to create project task');
    }

    return result.data.createUserProjectTask;
  }

  static async updateProject(input: { id: string; imageUrl?: string; title?: string; description?: string; datasetIds?: string[] }): Promise<UserProject> {
    const result = await client.graphql({
      query: UPDATE_PROJECT,
      variables: { input },
    }) as GraphQLResult<{ updateUserProject: UserProject }>;

    if (!result.data?.updateUserProject) {
      throw new Error('Failed to update project');
    }

    return result.data.updateUserProject;
  }

  static async deleteProject(id: string): Promise<void> {
    // Gather every child before touching the project row. getProjectConversations returns both
    // project chats and per-task conversations (all carry projectId). Listing errors propagate
    // here so a failed fetch aborts before we delete the project — never orphans data.
    const [tasks, conversations, schedules] = await Promise.all([
      ProjectService.getAllProjectTasks(id),
      ConversationService.getProjectConversations(id, 1000),
      ScheduleService.getProjectSchedules(id),
    ]);

    // Deactivate the schedules' live share links first — a deleted project's agents must not
    // keep serving public embeds. Best-effort (a share we can't flip never blocks the delete),
    // mirroring the deleteSchedule/removeRunner cascade; project delete is explicit enough
    // that no per-share confirmation is offered here.
    const runnerIds = schedules.flatMap((s) => (s.runners?.items || []).map((r) => r.id));
    if (runnerIds.length) {
      const shareIds = await ShareService.listActiveShareIdsForRunners(runnerIds);
      if (shareIds.length) await ShareService.deactivateShares(shareIds);
    }

    // Delete children first, matching each entity's own semantics (tasks hard; conversations and
    // schedules soft). Attempt all, then abort if ANY fails so nothing is left orphaned.
    const results = await Promise.allSettled([
      ...tasks.map((t) => ProjectService.deleteProjectTask(t.id)),
      ...conversations.map((c) => ConversationService.deleteConversation(c.id)),
      ...schedules.map((s) => ScheduleService.deleteSchedule(s.id)),
    ]);
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      throw new Error(`Failed to delete ${failed} of ${results.length} project item(s); project kept to avoid orphaned data`);
    }

    // Every child is gone — safe to remove the project row
    await client.graphql({
      query: DELETE_PROJECT,
      variables: { input: { id } },
    });
  }

  private static async getAllProjectTasks(projectId: string): Promise<UserProjectTask[]> {
    const all: UserProjectTask[] = [];
    let nextToken: string | null = null;
    do {
      const result = await client.graphql({
        query: GET_PROJECT_TASKS,
        variables: {
          filter: { projectId: { eq: projectId } },
          ...(nextToken ? { nextToken } : {}),
        },
      }) as GraphQLResult<{ listUserProjectTasks: { items: UserProjectTask[]; nextToken: string | null } }>;
      const page = result.data?.listUserProjectTasks;
      all.push(...(page?.items ?? []));
      nextToken = page?.nextToken ?? null;
    } while (nextToken);
    return all;
  }

  static async deleteProjectTask(id: string): Promise<void> {
    await client.graphql({
      query: DELETE_PROJECT_TASK,
      variables: { input: { id } },
    });
  }

  static async getProjectTasks(projectId: string): Promise<UserProjectTask[]> {
    const result = await client.graphql({
      query: GET_PROJECT_TASKS,
      variables: { filter: { projectId: { eq: projectId } } },
    }) as GraphQLResult<{ listUserProjectTasks: { items: UserProjectTask[] } }>;

    const items = result.data?.listUserProjectTasks?.items ?? [];
    return items.sort((a, b) => a.order - b.order);
  }

  static async updateProjectTask(input: { id: string; completedTask?: boolean; title?: string; description?: string }): Promise<UserProjectTask> {
    const result = await client.graphql({
      query: UPDATE_PROJECT_TASK,
      variables: { input },
    }) as GraphQLResult<{ updateUserProjectTask: UserProjectTask }>;

    if (!result.data?.updateUserProjectTask) {
      throw new Error('Failed to update project task');
    }

    return result.data.updateUserProjectTask;
  }
}
